
import React, { useEffect, useState } from 'react';
import { X, Plus, Minus, Trash2, ArrowRight } from 'lucide-react';
import { useCart } from '@/contexts/CartContext';
import { Link } from 'react-router-dom';

interface CartDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

const CartDrawer: React.FC<CartDrawerProps> = ({ isOpen, onClose }) => {
  const { cartItems, removeItem, updateQuantity, totalPrice, itemCount } = useCart();
  const [recentlyAdded, setRecentlyAdded] = useState<string[]>([]);

  // Detectar quando novos itens são adicionados ao carrinho
  useEffect(() => {
    const currentIds = cartItems.map(item => item.product.id);
    const newlyAdded = currentIds.filter(id => !recentlyAdded.includes(id));
    
    if (newlyAdded.length > 0) {
      setRecentlyAdded(prev => [...prev, ...newlyAdded]);
      
      // Limpar o efeito visual após 2 segundos
      setTimeout(() => {
        setRecentlyAdded(prev => prev.filter(id => !newlyAdded.includes(id)));
      }, 2000);
    }
  }, [cartItems]);

  const formatPrice = (price: number) => {
    return price.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    });
  };

  // Calcular economia total
  const totalSavings = cartItems.reduce((savings, item) => {
    if (item.product.discount && item.product.discount > 0) {
      const discountAmount = item.product.price * (item.product.discount / 100) * item.quantity;
      return savings + discountAmount;
    }
    return savings;
  }, 0);

  // Calcular subtotal sem desconto
  const subtotalWithoutDiscount = cartItems.reduce((total, item) => {
    return total + (item.product.price * item.quantity);
  }, 0);

  // Verificar frete grátis
  const freeShippingThreshold = 200;
  const hasFreeShipping = totalPrice >= freeShippingThreshold;
  const amountForFreeShipping = freeShippingThreshold - totalPrice;

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity"
          onClick={onClose}
        />
      )}
      
      {/* Drawer */}
      <div className={`fixed top-0 right-0 w-full sm:w-[420px] h-full bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out flex flex-col ${
        isOpen ? 'translate-x-0' : 'translate-x-full'
      }`}>
        {/* Header com Gradiente */}
        <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-md">
          <div>
            <h2 className="text-xl font-bold">Seu Carrinho</h2>
            <p className="text-sm text-blue-100">{itemCount} {itemCount === 1 ? 'item' : 'itens'}</p>
          </div>
          <button 
            onClick={onClose} 
            className="text-white hover:bg-white/20 p-2 rounded-full transition-colors"
            aria-label="Fechar carrinho"
          >
            <X size={24} />
          </button>
        </div>
        
        {/* Cart Items */}
        <div className="flex-1 overflow-auto p-4">
          {cartItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center px-4">
              <div className="w-24 h-24 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                <ShoppingCartIcon className="w-12 h-12 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Seu carrinho está vazio</h3>
              <p className="text-sm text-gray-600 mb-6">Adicione produtos para começar suas compras</p>
              <button 
                onClick={onClose} 
                className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors shadow-md"
              >
                Continuar Comprando
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {cartItems.map((item) => {
                const finalPrice = item.product.discount 
                  ? item.product.price * (1 - item.product.discount / 100)
                  : item.product.price;
                
                return (
                  <div 
                    key={item.product.id} 
                    className={`p-4 rounded-lg border-2 transition-all ${
                      recentlyAdded.includes(item.product.id) 
                        ? 'bg-green-50 border-green-300 shadow-md' 
                        : 'bg-white border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex gap-4">
                      {/* Product Image */}
                      <img 
                        src={item.product.imageUrl} 
                        alt={item.product.name} 
                        className="w-24 h-24 object-cover rounded-lg border border-gray-200 flex-shrink-0"
                      />
                      
                      {/* Product Details */}
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-semibold text-gray-900 line-clamp-2 mb-1">
                          {item.product.name}
                        </h3>
                        
                        {/* Discount Badge */}
                        {item.product.discount && item.product.discount > 0 && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-bold bg-red-100 text-red-800 mb-2">
                            🔥 -{item.product.discount}% OFF
                          </span>
                        )}
                        
                        {item.variant && (
                          <p className="text-xs text-gray-500 mb-2">{item.variant.name}</p>
                        )}
                        
                        {/* Prices */}
                        <div className="space-y-1 mb-3">
                          <div className="flex items-center justify-between text-xs text-gray-600">
                            <span>Preço unitário:</span>
                            <div className="flex items-baseline gap-1.5">
                              <span className="font-semibold text-gray-900">{formatPrice(finalPrice)}</span>
                              {item.product.discount && item.product.discount > 0 && (
                                <span className="text-gray-400 line-through">{formatPrice(item.product.price)}</span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-700">Subtotal:</span>
                            <span className="text-base font-bold text-navy-700">
                              {formatPrice(finalPrice * item.quantity)}
                            </span>
                          </div>
                        </div>
                        
                        {/* Quantity Controls and Actions */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center border-2 border-gray-300 rounded-lg overflow-hidden">
                            <button 
                              onClick={() => updateQuantity(item.product.id, item.quantity - 1)}
                              className="px-3 py-2 text-gray-700 hover:bg-gray-100 active:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              disabled={item.quantity <= 1}
                              aria-label="Diminuir quantidade"
                            >
                              <Minus size={16} />
                            </button>
                            <span className="px-4 py-2 text-base font-semibold text-gray-900 min-w-[3rem] text-center">
                              {item.quantity}
                            </span>
                            <button 
                              onClick={() => updateQuantity(item.product.id, item.quantity + 1)}
                              className="px-3 py-2 text-gray-700 hover:bg-gray-100 active:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              disabled={item.quantity >= item.product.stock}
                              aria-label="Aumentar quantidade"
                            >
                              <Plus size={16} />
                            </button>
                          </div>
                          
                          <button 
                            onClick={() => removeItem(item.product.id)}
                            className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            aria-label="Remover item"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                        
                        {/* Stock Warning */}
                        {item.quantity >= item.product.stock && (
                          <div className="mt-2 flex items-center gap-1 text-xs text-orange-600 font-medium">
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                            <span>Quantidade máxima disponível</span>
                          </div>
                        )}
                        
                        {item.product.stock <= 5 && item.quantity < item.product.stock && (
                          <div className="mt-2 flex items-center gap-1 text-xs text-orange-600 font-medium">
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                            <span>Apenas {item.product.stock - item.quantity} restantes</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        
        {/* Footer */}
        {cartItems.length > 0 && (
          <div className="border-t-2 border-gray-200 p-4 bg-gray-50">
            {/* Free Shipping Alert */}
            {!hasFreeShipping && amountForFreeShipping > 0 && (
              <div className="mb-4 p-3 bg-blue-50 border-2 border-blue-200 rounded-lg">
                <p className="text-sm text-blue-900 font-medium">
                  🚚 Faltam <strong className="text-blue-700">{formatPrice(amountForFreeShipping)}</strong> para <strong>frete grátis!</strong>
                </p>
              </div>
            )}
            
            {hasFreeShipping && (
              <div className="mb-4 p-3 bg-green-50 border-2 border-green-200 rounded-lg">
                <p className="text-sm text-green-900 font-bold flex items-center gap-2">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Parabéns! Você ganhou frete grátis! 🎉
                </p>
              </div>
            )}
            
            {/* Summary */}
            <div className="space-y-2 mb-4">
              {/* Savings */}
              {totalSavings > 0 && (
                <div className="flex items-center justify-between text-sm pb-2 border-b border-gray-200">
                  <span className="text-green-700 font-semibold flex items-center gap-1">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Você está economizando:
                  </span>
                  <span className="text-green-700 font-bold">{formatPrice(totalSavings)}</span>
                </div>
              )}
              
              {/* Subtotal */}
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Subtotal</span>
                <span className="font-semibold text-gray-900">{formatPrice(totalPrice)}</span>
              </div>
              
              {/* Shipping */}
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Frete</span>
                {hasFreeShipping ? (
                  <span className="text-green-700 font-bold">GRÁTIS 🎉</span>
                ) : (
                  <span className="text-gray-500">A calcular</span>
                )}
              </div>
              
              {/* Total */}
              <div className="pt-3 border-t-2 border-gray-300">
                <div className="flex items-center justify-between">
                  <span className="text-base font-bold text-gray-900">Total</span>
                  <span className="text-2xl font-bold text-navy-700">{formatPrice(totalPrice)}</span>
                </div>
              </div>
            </div>
            
            {/* Continue Shopping Button */}
            <button
              onClick={onClose}
              className="w-full py-3 mb-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              Continuar Comprando
            </button>
            
            {/* Checkout Button */}
            <Link
              to="/checkout"
              onClick={onClose}
              className="flex items-center justify-center w-full py-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl font-bold text-lg hover:from-orange-600 hover:to-orange-700 transition-all shadow-lg hover:shadow-xl active:scale-[0.98] transform"
            >
              <ShoppingCartIcon className="w-5 h-5 mr-2" />
              Finalizar Compra
              <ArrowRight size={20} className="ml-2" />
            </Link>
          </div>
        )}
      </div>
    </>
  );
};

// Cart icon component for empty state
const ShoppingCartIcon = ({ className }: { className?: string }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round" 
    className={className}
  >
    <circle cx="9" cy="21" r="1"></circle>
    <circle cx="20" cy="21" r="1"></circle>
    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
  </svg>
);

export default CartDrawer;
